// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Close from "@stanion/XDL/Icons/Close";
import Info from "@stanion/XDL/Icons/Info";
import { color, ellipsis, layoutContainer, xScreen } from "@stanion/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { CSSTransition } from "react-transition-group";
import { css } from "styled-components";

export const enum fields {
    message = "message",
    backgroundColor = "backgroundColor",
    colorField = "colorField",
}

interface HeaderBannerNotificationProps extends WidgetProps {
    fields: {
        [fields.message]: string;
        [fields.backgroundColor]: string;
        [fields.colorField]: string;
    };
}

type Props = HeaderBannerNotificationProps;

export interface HeaderBannerNotificationStyles {
    container?: InjectableCss;
    bannerContent?: InjectableCss;
    messageWrapper?: InjectableCss;
    infoIcon?: IconPresentationProps;
    closeIcon?: IconPresentationProps;
    message?: TypographyProps;
}

export const headerBannerNotificationStyles: HeaderBannerNotificationStyles = {
    container: {
        css: css`
            ${({ backgroundColor }: any) =>
                css`
                    background: ${backgroundColor || color("secondary.main")};
                `}

            .global-message-enter {
                opacity: 0;
            }

            .global-message-enter-active {
                opacity: 1;
            }
            .global-message-exit {
                opacity: 1;
            }

            .global-message-exit-active {
                animation: containerAnimation 500ms ease forwards;
                transform-origin: left;
            }

            @keyframes containerAnimation {
                from {
                    opacity: 1;
                    transform: translate(0, 0);
                }
                to {
                    opacity: 0;
                    transform: translate(0, -50px);
                }
            }
        `,
    },
    bannerContent: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            height: 40px;
            padding: 0 9px;
            font-size: 14px;
            letter-spacing: 0;
            line-height: 22px;
            position: relative;
            ${layoutContainer}

            p {
                margin: 0;
            }

            ${xScreen("lte", "xs")} {
                height: 60px;
                justify-content: start;
            }
        `,
    },
    messageWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            ${xScreen("lte", "xs")} {
                width: calc(100% - 40px);
            }
        `,
    },
    infoIcon: {
        css: css`
            margin: 5px 0px;
            width: 20px;
            ${({ colorField }: any) =>
                css`
                    svg {
                        fill: ${colorField || color("common.ui6")};
                    }
                `}
            ${xScreen("lte", "xs")} {
                margin: 7px 5px;
            }
        `,
        size: 17,
    },
    closeIcon: {
        css: css`
            position: absolute;
            right: 16px;

            ${xScreen("lte", "xs")} {
                right: 16px;
                top: 9px;
            }
            ${({ colorField }: any) =>
                css`
                    svg {
                        fill: ${colorField || color("common.ui6")};
                    }
                `}
            ${xScreen("lte", "xs")} {
                margin: 5px;
            }
        `,
        size: 12,
    },
    message: {
        css: css`
            & > p {
                ${ellipsis(2)}
                font-size: 14px;
                ${xScreen("lte", "xs")} {
                    line-height: 1;
                }
                ${({ colorField }: any) =>
                    css`
                        color: ${colorField || color("text")};
                    `}
            }
        `,
    },
};

const styles = headerBannerNotificationStyles;

const HeaderBannerNotification: React.FC<Props> = ({ fields: { message, backgroundColor, colorField } }) => {
    const [visible, setVisible] = React.useState(true);

    const handleCloseBanner = () => {
        setVisible(false);
    };

    return (
        <>
            <StyledWrapper {...styles.container} {...{ backgroundColor }}>
                <CSSTransition
                    in={visible}
                    timeout={{
                        enter: 0,
                        exit: 250,
                    }}
                    unmountOnExit
                    classNames="global-message"
                >
                    <StyledWrapper {...styles.bannerContent}>
                        <StyledWrapper {...styles.messageWrapper}>
                            <Icon src={Info} {...styles.infoIcon} {...{ colorField }} role="img" />
                            <Typography {...styles.message} {...{ colorField }}>
                                {parse(message, parserOptions)}
                            </Typography>
                        </StyledWrapper>
                        <Icon
                            src={Close}
                            {...styles.closeIcon}
                            {...{ colorField }}
                            role="img"
                            onClick={handleCloseBanner}
                        />
                    </StyledWrapper>
                </CSSTransition>
            </StyledWrapper>
        </>
    );
};

const headerAlertBanner: WidgetModule = {
    component: HeaderBannerNotification,
    definition: {
        group: "Header",
        displayName: "Header Banner Notification",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.colorField,
                displayName: "Font Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.message,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                isRequired: true,
            },
        ],
    },
};

export default headerAlertBanner;
